import { Injectable } from '@angular/core';
import { Observable, Observer } from 'rxjs';
import { AbstractControl, FormGroup, FormControl, Validators } from '@angular/forms';
import { setRequiredValidation, getFieldValidations } from './shared/validators/custom-validations.validator';
import { validatorFuncs } from './shared/validators/validation.factory';
import { requiredByRuleWrapper } from './shared/validators/backend-validation.validator';
import { ParameterizedFuncs, RequiredByRule } from './configs/form-config-types.interface';

@Injectable({
  providedIn: 'root'
})
export class FormTabService {
  public formJson: any
  public formValuesJson: any
  private ruleValidatedControls = new Map<string, AbstractControl>();

  constructor() { }

  initForm(formJsonConfig: any, formValues: any): Observable<any> {
    return new Observable((observer: Observer<any>) => {
      this.formJson = JSON.parse(formJsonConfig);
      this.formValuesJson = JSON.parse(formValues);
      if (this.formJson === null) { return; }
      // If formValues is a JSON array, multiple forms must be initialized
      if (Array.isArray(this.formValuesJson) && this.formValuesJson.length > 0) {
        const len = this.formValuesJson.length;
        const formValues = this.formValuesJson;
        for (let i = 0; i < len; i++) {
          this.formValuesJson = formValues[i];
          // id - caimid, locid, etc
          observer.next({ id: this.formValuesJson.id, form: this.toFormGroup(this.formJson, this.formValuesJson), orderedFormJson: this.formJson })
        }
      } else {
        observer.next({ form: this.toFormGroup(this.formJson, this.formValuesJson), orderedFormJson: this.formJson })
      }
      observer.complete();
    })
  }

  findControlByName(control, name) {
    if (control.controls) {
      for (const key in control.controls) {
        if (key == name) return control.controls[key];
        const result = this.findControlByName(control.controls[key], name);
        if (result) return result;
      }
    }
    return null;
  }

  findCoverageControlByName(formGroup, name) {
    for (let form in formGroup.controls) {
      const coverage = formGroup.get(form).get("Coverages")?.controls?.find(coverage => coverage.get("CoverageNameCd").value == name);
      if (coverage) {
        return coverage.get("CoverageSelectedInd");
      }
    }
    return undefined;
  }

  /**
   * Setup the parameterized validations on the thisFormGroup based on the formModel
   * @param formModel the form model with the parameterized validations
   * @param thisFormGroup the form group created from the form model (which will have the parameterized validations setup)
   * @param targetFormGroup the form group with the target field to validate against (defaults to the same as thisFormGroup)
   */
  setupParameterizedValidations(formModel: any, thisFormGroup: FormGroup, targetFormGroup: FormGroup = thisFormGroup): void {
    formModel.forEach(section => {
      section.parameters.forEach(field => {
        if (!field.validations?.parameterizedFuncs) return;
        const thisControl = this.findControlByName(thisFormGroup, field.name);
        if (!thisControl) return;
        const baseValidations = getFieldValidations(field);
        
        for (const [fn, instances] of Object.entries(field.validations?.parameterizedFuncs as ParameterizedFuncs)) {
          for (const instance of Object.values(instances)) {
            const paramArgs = [];
            let hasFieldReference = false;
            for (const arg of instance) {
              if (["$", "#"].includes(arg[0])) {
                let targetControl = null;
                if (arg[0] == "$") targetControl = this.findControlByName(targetFormGroup, arg.slice(1).trim());
                else if (arg[0] == "#") targetControl = this.findCoverageControlByName(targetFormGroup, arg.slice(1).trim());
                if (targetControl) {
                  hasFieldReference = true;
                  paramArgs.push(targetControl);
                }
              } else {
                paramArgs.push(arg);
              }
            }
            if (hasFieldReference && paramArgs[0]) {
              if (fn == "enabledByValues") {
                const numOfInstances = Object.keys(instances).length;
                baseValidations.push((validatorFuncs[fn] as any)(...paramArgs, numOfInstances));
              } else {
                baseValidations.push(validatorFuncs[fn](...paramArgs));
              }
            } else if (!hasFieldReference && paramArgs.length > 0 && validatorFuncs[fn] && fn == "validateDateWithinRange") {
                baseValidations.push((validatorFuncs[fn] as any)(...paramArgs))
            }
          }
        }
        
        // Get existing validators and combine with new ones (Angular 8 doesn't have addValidators)
        const existingValidators = thisControl.validator ? [thisControl.validator] : [];
        const allValidators = [...existingValidators, ...baseValidations];
        thisControl.setValidators(Validators.compose(allValidators));
        thisControl.updateValueAndValidity({ onlySelf: true, emitEvent: true });
      });
    });
  }

  setupRuleBasedValidations(
    formModel: any,
    thisFormGroup: FormGroup,
    getOpinions: () => { ruleName: string; outcome?: string }[] = () => [],
    formId?: number | string
  ): void {
    formModel.forEach(section => {
      section.parameters?.forEach(field => {
        const requiredByRule: RequiredByRule = field.validations?.requiredByRule;
        if (!requiredByRule?.rule) return;

        const thisControl = this.findControlByName(thisFormGroup, field.name);
        if (!thisControl) return;

        const validationKey = `${formId != null ? formId : 'global'}:${field.name}:${requiredByRule.rule}`;
        if (this.ruleValidatedControls.has(validationKey)) {
          this.ruleValidatedControls.delete(validationKey);
        }

        const invert = requiredByRule.invert ?? false;
        const getIsRuleActive = () => {
          const opinions = getOpinions() ?? [];
          return opinions.some(o => o.ruleName === requiredByRule.rule);
        };

        const baseValidations = getFieldValidations(field);
        baseValidations.push(requiredByRuleWrapper(getIsRuleActive, invert));

        const existingValidators = thisControl.validator ? [thisControl.validator] : [];
        thisControl.setValidators(Validators.compose([...existingValidators, ...baseValidations]));
        this.ruleValidatedControls.set(validationKey, thisControl);
        thisControl.updateValueAndValidity({ onlySelf: true, emitEvent: true });
      });
    });
  }

  clearRuleBasedValidations(formId: number | string): void {
    const prefix = `${formId}:`;
    Array.from(this.ruleValidatedControls.keys()).forEach(key => {
      if (key.startsWith(prefix)) {
        this.ruleValidatedControls.delete(key);
      }
    });
  }

  refreshRuleBasedValidations(): void {
    this.ruleValidatedControls.forEach(control => {
      control.updateValueAndValidity({ onlySelf: true, emitEvent: false });
    });
  }

  toFormGroup(formModel, formValues): any {
    let formGroup = {};
    let formControls = [];

    formModel.forEach(field => {
      if (field.type == 'object') {
        if (field.parameters != null) {
          formGroup[field.name] = this.toFormGroupFromArr(field.parameters);
          if (field.conditionalRequirements != null) {
            const conditionalRequirementsArray = field.conditionalRequirements;
            const validators = [];
            for (const conditionalRequirement of conditionalRequirementsArray) {
              validators.push(setRequiredValidation(conditionalRequirement, formGroup, formModel))
            }
            formGroup[field.name].setValidators(Validators.compose(validators));
          }
        }
      } else {
        formControls.push({ name: field.name, control: this.appendFieldFormControlToObject(field)[field.name] })
      }
    });

    let form = new FormGroup(formGroup);
    formControls.forEach(f => {
      form.addControl(f.name, f.control)
    });
    // check if any custom validation functions have a field control as a parameter
    this.setupParameterizedValidations(formModel, form);
    return form;
  }

  toFormGroupFromArr(arr) {
    return new FormGroup(arr.reduce((acc, cur) => {
      return this.appendFieldFormControlToObject(cur, acc)
    }, {}))
  }

  appendFieldFormControlToObject(field, obj = {}) {
    if (field.type == 'object') {
      obj[field.name] = this.toFormGroupFromArr(field.paramters);
    }
    else {
      const controlConfig = {
        value: this.formValuesJson[field.name],
        disabled: field.disable
      }
      const validations = getFieldValidations(field)
      obj[field.name] = new FormControl(controlConfig, { validators: validations, updateOn: "change" });
    }
    return obj;
  }

}
